import { cx } from 'linaria';
import * as React from 'react';
import DayPicker, { DateUtils, DayModifiers } from 'react-day-picker';

import dayjs from 'dayjs';
import { IconCalendarSmall, IconRemoveSmall } from '../foundation/icons';
import { FocusScope, Input, InputProps, Portal, states, statesSelected, usePositioner, useTheme } from '../index';
import { makeTestId } from '../utils/common';

// import "react-day-picker/lib/style.css";
import * as styles from './date-picker.styles';

export interface DatePickerProps extends Omit<InputProps, 'value' | 'onChange' | 'min' | 'max'> {
    value?: Date;
    onChange: (date: Date) => void;
    disabled?: boolean;
    min?: Date;
    max?: Date;
    placeholder?: string;
    testId?: string;
    id?: string;
}

export const DatePicker = (props: DatePickerProps) => {
    const { value, onChange, disabled, size, fullWidth, error, placeholder, min, max, id, testId } = props;

    const { isVisible, handleInvisible, handleVisible, handleKeyDown, handleOverlayClick } = useDatePopover();
    const { getPopperProps, getReferenceProps } = usePositioner({
        placement: 'bottom-start',
    });
    const theme = useTheme();
    const dateDisplay = formatDate(value, 'DD/MM/YYYY');
    const month = React.useMemo(() => {
        if (dayjs(value).isValid()) {
            return value;
        }

        return new Date();
    }, [value]);

    const handleDayClick = (day: Date, modifiers: DayModifiers) => {
        if (modifiers.disabled) {
            return;
        }

        onChange(day);
        handleInvisible();
    };

    const handleClear = () => {
        onChange(null);
    };

    return (
        <>
            <Input
                {...props}
                {...getReferenceProps()}
                readOnly
                placeholder={placeholder}
                onClick={handleVisible}
                onFocus={handleVisible}
                onKeyDown={handleKeyDown}
                value={dateDisplay}
                disabled={disabled}
                size={size}
                fullWidth={fullWidth}
                error={error}
                id={id}
                data-testid={testId}
                endAdornmentSlot={
                    <div className={styles.clearContainer}>
                        <IconCalendarSmall />
                        {value && !disabled && <IconRemoveSmall onClick={handleClear} className={styles.clearDate} />}
                    </div>
                }
            />

            {isVisible && (
                <Portal data-theme={theme}>
                    <div onClick={handleOverlayClick} className={styles.overlay} />

                    <FocusScope contain>
                        <div {...getPopperProps()} className={cx(styles.popover)}>
                            <DayPicker
                                showOutsideDays
                                fixedWeeks
                                enableOutsideDaysClick={false}
                                selectedDays={value}
                                month={month}
                                disabledDays={{
                                    before: min,
                                    after: max,
                                }}
                                onDayClick={handleDayClick}
                                data-testid={makeTestId(testId, 'datepicker')}
                            />
                        </div>
                    </FocusScope>
                </Portal>
            )}
        </>
    );
};

interface DatePreset {
    label: string;
    from: Date;
    to: Date;
}
export interface DateRangePickerProps extends Omit<InputProps, 'value' | 'onChange' | 'min' | 'max'> {
    value?: {
        from?: Date;
        to?: Date;
    };
    onChange: ({ from, to }: { from: Date; to: Date }) => void;
    presets?: Record<string, DatePreset>;
    testId?: string;
    disabled?: boolean;
    min?: Date;
    max?: Date;
    id?: string;
    placeholder?: string;
    selectFrom?: Date;
}

export const DateRangePicker = (props: DateRangePickerProps) => {
    const { value, onChange, presets = {}, placeholder, min, max, disabled, id, selectFrom, testId } = props;
    const from = value?.from;
    const to = value?.to;

    const { isVisible, handleInvisible, handleVisible, handleKeyDown, handleOverlayClick } = useDatePopover();
    const [enteredTo, setEnteredTo] = React.useState<Date>(to);

    const { getPopperProps, getReferenceProps } = usePositioner({
        placement: 'bottom-start',
    });

    const onDayClick = (day: Date) => {
        if (from && to && day >= from && day <= to) {
            onChange({
                from: day,
                to: null,
            });
            setEnteredTo(null);

            return;
        }

        if (isSelectingFirstDay(from, to, day)) {
            onChange({
                from: day,
                to: null,
            });
            setEnteredTo(null);

            return;
        }

        onChange({
            from,
            to: day,
        });
        setEnteredTo(day);
        handleInvisible();
    };

    const handleDayMouseEnter = (day: Date) => {
        if (!isSelectingFirstDay(from, to, day)) {
            setEnteredTo(day);
        }
    };

    const handlePresetChange = (from: Date, to: Date) => {
        onChange({
            from,
            to,
        });

        setEnteredTo(to);
        handleInvisible();
    };

    const handleClear = () => {
        onChange({
            from: null,
            to: null,
        });
    };

    const dateDisplay = formatRange(from, to, 'DD/MM/YYYY');
    const modifiers = { start: from, end: enteredTo };

    const presetsArray = Object.values(presets);
    const hasPresets = presetsArray.length > 0;

    const presetDisplay = checkPresetDisplay(from, to, presetsArray);
    const inputValue = presetDisplay ?? dateDisplay;
    const theme = useTheme();

    return (
        <>
            <Input
                style={{
                    minWidth: 240,
                }}
                {...props}
                {...getReferenceProps()}
                readOnly
                placeholder={placeholder}
                disabled={disabled}
                value={inputValue}
                onFocus={handleVisible}
                onClick={handleVisible}
                onKeyDown={handleKeyDown}
                id={id}
                data-testid={testId}
                endAdornmentSlot={
                    <div className={styles.clearContainer}>
                        <IconCalendarSmall width="16" height="16" />
                        {(value.to || value.from) && !disabled && (
                            <IconRemoveSmall onClick={handleClear} className={styles.clearDate} />
                        )}
                    </div>
                }
            />

            {isVisible && (
                <Portal data-theme={theme}>
                    <div onClick={handleOverlayClick} className={styles.overlay} />

                    <FocusScope contain>
                        <div {...getPopperProps()} className={cx(styles.popover)}>
                            {hasPresets && (
                                <div className={cx(styles.presetWrapper)}>
                                    {presetsArray.map((preset) => (
                                        <PresetButton
                                            key={preset.label}
                                            preset={preset}
                                            selected={isSameDate(from, preset.from) && isSameDate(to, preset.to)}
                                            onChange={handlePresetChange}
                                        />
                                    ))}
                                </div>
                            )}

                            <DayPicker
                                showOutsideDays
                                fixedWeeks
                                enableOutsideDaysClick={false}
                                className={cx('DayPicker-range')}
                                numberOfMonths={2}
                                modifiers={modifiers}
                                month={from}
                                fromMonth={selectFrom}
                                selectedDays={[from, { from, to: enteredTo }]}
                                onDayClick={onDayClick}
                                onDayMouseEnter={handleDayMouseEnter}
                                disabledDays={{
                                    before: min,
                                    after: max,
                                }}
                                data-testid={makeTestId(testId, 'daterangepicker')}
                            />
                        </div>
                    </FocusScope>
                </Portal>
            )}
        </>
    );
};

DateRangePicker.PRESETS = {
    ALL: {
        label: 'All',
        from: null,
        to: null,
    },
    TODAY: {
        label: 'Today',
        from: dayjs().toDate(),
        to: dayjs().toDate(),
    },

    LAST_7_DAYS: {
        label: 'Last 7 days',
        from: dayjs().subtract(7, 'days').toDate(),
        to: dayjs().toDate(),
    },

    LAST_14_DAYS: {
        label: 'Last 14 days',
        from: dayjs().subtract(14, 'days').toDate(),
        to: dayjs().toDate(),
    },

    LAST_30_DAYS: {
        label: 'Last 30 days',
        from: dayjs().subtract(30, 'days').toDate(),
        to: dayjs().toDate(),
    },

    NEXT_7_DAYS: {
        label: 'Next 7 days',
        to: dayjs().add(7, 'days').toDate(),
        from: dayjs().toDate(),
    },

    NEXT_14_DAYS: {
        label: 'Next 14 days',
        to: dayjs().add(14, 'days').toDate(),
        from: dayjs().toDate(),
    },
};

// Helpers

const useDatePopover = () => {
    const [isVisible, setIsVisible] = React.useState(false);

    const handleInvisible = React.useCallback(() => {
        setIsVisible(false);
    }, []);

    const handleVisible = React.useCallback(() => {
        setIsVisible(true);
    }, []);

    const handleKeyDown = React.useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === 'Escape') {
                handleInvisible();
            }
        },
        [handleInvisible],
    );

    const handleOverlayClick = React.useCallback(
        (event: React.MouseEvent) => {
            if (event.target === event.currentTarget) {
                handleInvisible();
            }
        },
        [handleInvisible],
    );

    return {
        isVisible,
        handleVisible,
        handleInvisible,
        handleKeyDown,
        handleOverlayClick,
    };
};

const PresetButton = ({
    preset,
    onChange,
    selected,
}: {
    preset: DatePreset;
    onChange: (from: Date, to: Date) => void;
    selected: boolean;
}) => {
    return (
        <button
            className={cx(styles.presetButton, states, selected && statesSelected)}
            type="button"
            aria-selected={selected}
            onClick={() => onChange(preset.from, preset.to)}
        >
            {preset.label}
        </button>
    );
};

function formatDate(date: Date, format: string) {
    try {
        if (dayjs(date).isValid()) {
            return dayjs(date).format(format);
        }

        return '';
    } catch (error) {
        return '';
    }
}

function formatRange(from: Date, to: Date, format: string) {
    const fromDisplay = formatDate(from, format);
    const toDisplay = formatDate(to, format);

    if (!fromDisplay) {
        return '';
    }

    return `${fromDisplay} - ${toDisplay}`;
}

function isSelectingFirstDay(from: Date, to: Date, day: Date) {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
}

function isSameDate(a: Date, b: Date) {
    try {
        // Checks for an All date preset. null : null means All is selected.
        // DayJS returns an invalid date for null and today for undefined.
        if (a != null && b == null) {
            return false;
        }
        if (a == null && b == null) {
            return true;
        }

        return dayjs(a).isSame(dayjs(b), 'day');
    } catch (err) {
        return false;
    }
}

function checkPresetDisplay(from: Date, to: Date, presets: Array<DatePreset>) {
    return presets.find((preset) => isSameDate(from, preset.from) && isSameDate(to, preset.to))?.label;
}
